/** @jsxImportSource @emotion/react */
import React, { useState, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomGrid from 'components/grids/CustomGrid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';
import UtMatrixHookupTagPopup from '../popup/UtMatrixHookupTagPopup';
import { CommonUtil } from 'utils/CommonUtil';
import {
  DefaultNoneNumberCellTemplate,
  DefaultZeroNumberCellTemplate,
} from 'components/grids/NumberCellRenderer';

interface Props {
  detailRowData?: any;
}

/**
 * @desc 결과보기 > [상세보기] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.09.29   jshwang12       [고도화] 표준사용량 로직 변경 (표준설비코드 기준 라이브러리의 평균값 노출(저장X), 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함))
 *                              [고도화] 열 순서, Size 열 명칭 변경 (O.D -> OUT, I.D -> IN)
 *                              [고도화] Exhaust air : Collector type, Temperature 항목 추가
 *                              [고도화] Supply air : Temperature 추가
 * 2025.09.29   jshwang12       [고도화] 전력/CDA 실사용량 추가 (EUM Tag 최대 수치값 노출(저장X))
 * 2025.09.29   jshwang12       [고도화] CDA, N2 단위 변경 (Nl/min)
 * 2025.10.02   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2025.10.02   jshwang12       숫자 표현 방식에서 Power Factor만 예외로 소수점 2자리까지 표현(3자리에서 반올림 처리)
 */
const DetailSummaryContent = ({ detailRowData }: Props, ref) => {
  const [flexRef, setFlexRef] = useState<any>();
  const { t } = useTranslation();
  const [isOpenHookupTagPopup, setOpenHookupTagPopup] = useState<boolean>(false);
  const [hookupTagCondition, setHookupTagCondition] = useState<any>();

  useImperativeHandle(ref, () => ({
    btnDetailExcelExport: () => {
      btnDetailExcelExport();
    },
  }));

  const layoutDefinition = [
    {
      binding: 'no',
      header: String(t('com.label.NO', 'NO')),
      width: 40,
      isReadOnly: true,
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
    },
    {
      binding: 'utmId',
      header: String(t('ut.label.UTM ID', 'UTM ID')),
      minWidth: 80,
      width: 100,
      align: 'center',
    },
    {
      binding: 'prdnProcCdNm',
      header: String(t('ut.label.공정', '공정')),
      minWidth: 80,
      width: 100,
      align: 'center',
    },
    {
      header: String(t('ut.label.AREA', 'AREA')),
      columns: [
        {
          binding: 'bizAreaNm',
          header: ' ',
          minWidth: 65,
          width: 68,
          align: 'center',
        },
      ],
    },
    {
      binding: 'eqclNm',
      header: String(t('ut.label.설비군', '설비군')),
      minWidth: 80,
      width: 100,
    },
    {
      binding: 'dtalProcCd',
      header: String(t('ut.label.세부공정', '세부공정')),
      minWidth: 80,
      width: 110,
    },
    {
      header: String(t('ut.label.Facility Location', 'Facility Location')),
      collapseTo: 'bldgFloCd',
      isCollapsed: true,
      columns: [
        {
          binding: 'bldgFloCd',
          header: String(t('ut.label.Floor', 'Floor')),
          minWidth: 80,
          width: 120,
          align: 'center',
        },
        {
          binding: 'istlLocNm',
          header: String(t('ut.label.Room', 'Room')),
          minWidth: 80,
          width: 100,
        },
      ],
    },
    {
      header: String(t('ut.label.Line', 'Line')),
      columns: [
        {
          binding: 'prdnLnNm',
          header: ' ',
          minWidth: 60,
          width: 68,
          align: 'center',
        },
      ],
    },
    {
      header: String(t('ut.label.Sub Line', 'Sub Line')),
      columns: [
        {
          binding: 'prdnPrlLnNm',
          header: '　',
          minWidth: 60,
          width: 68,
          align: 'center',
        },
      ],
    },
    {
      binding: 'eqpMainNm',
      header: String(t('ut.label.Main', 'Main')),
      minWidth: 80,
      width: 100,
    },
    {
      binding: 'eqpMchNm',
      header: String(t('ut.label.Machine', 'Machine')),
      minWidth: 80,
      width: 120,
    },
    {
      header: String(t('ut.label.Unit', 'Unit')),
      columns: [
        {
          binding: 'eqpUntNm',
          header: ' ',
          minWidth: 60,
          width: 68,
        },
      ],
    },
    {
      binding: 'insTpCdNm',
      header: String(t('ut.label.작성구분', '작성구분')),
      minWidth: 80,
      width: 100,
      align: 'center',
    },
    {
      binding: 'stndEqpId',
      header: String(t('ut.label.표준설비코드', '표준설비코드')),
      minWidth: 80,
      width: 140,
      align: 'center',
    },
    {
      header: String(t('ut.label.Library', 'Library')),
      columns: [
        {
          binding: 'utmLibId',
          header: String(t('ut.label.ID', 'ID')),
          minWidth: 80,
          width: 100,
        },
        {
          binding: 'utmLibVerNo',
          header: String(t('ut.label.Version', 'Version')),
          minWidth: 80,
          width: 100,
        },
      ],
    },
    {
      header: String(t('ut.label.Hookup Tagging', 'Hookup Tagging')),
      collapseTo: 'machEqpNm',
      columns: [
        {
          binding: 'machEqpNm',
          header: String(t('ut.label.Mech', 'Mech')),
          minWidth: 80,
          width: 120,
          cssClass: 'WijmoFind',
          cellTemplate: (params) => `
                <span>${params.value || ''}</span>
                ${params.value && params.item.tagIssuYn === 'Y' ? '<Button id="machEqpNm"/>' : ''} 
            `,
        },
        {
          binding: 'elpwEqpNm',
          header: String(t('ut.label.Elec', 'Elec')),
          minWidth: 80,
          width: 120,
          cssClass: 'WijmoFind',
          cellTemplate: (params) => `
                <span>${params.value || ''}</span>
                ${params.value && params.item.tagIssuYn === 'Y' ? '<Button id="elpwEqpNm" />' : ''}
            `,
        },
      ],
    },
    {
      header: String(t('ut.label.설비 Qty', '설비 Qty')),
      columns: [
        {
          binding: 'machEqpQty',
          header: String(t('ut.label.Mech', 'Mech')),
          minWidth: 80,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
        {
          binding: 'elpwEqpQty',
          header: String(t('ut.label.Elec', 'Elec')),
          minWidth: 80,
          width: 100,
          align: 'right',
          cellTemplate: DefaultZeroNumberCellTemplate,
        },
      ],
    },
    {
      header: String(t('ut.label.Electricity', 'Electricity')),
      collapseTo: 'vltgNvl',
      columns: [
        {
          header: String(t('ut.label.Voltage', 'Voltage')),
          columns: [
            {
              binding: 'vltgNvl',
              header: '[V]',
              minWidth: 60,
              width: 75,
              align: 'center',
            },
          ],
        },
        {
          header: String(t('ut.label.Phase', 'Phase')),
          columns: [
            {
              binding: 'elpwPhasCd',
              header: '[Φ]',
              minWidth: 60,
              width: 75,
              align: 'center',
            },
          ],
        },
        {
          header: String(t('ut.label.Wires', 'Wires')),
          columns: [
            {
              binding: 'ctwTpCdNm',
              header: '[EA]',
              minWidth: 60,
              width: 80,
              align: 'center',
            },
          ],
        },
        {
          // [25.09.25] Power Factor 노출 (고도화)
          header: String(t('ut.label.Power Factor', 'Power Factor')),
          columns: [
            {
              binding: 'pwftNvl',
              header: '-',
              minWidth: 60,
              width: 86,
              align: 'right',
              // [25.10.01] Power Factor는 소수점 2자리까지 표현 (3자리에서 반올림 처리)
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params, 2)
                  : DefaultZeroNumberCellTemplate(params, 2),
            },
          ],
        },
        /*
        // [25.09.23] 화면에서 삭제 (고도화)
        {
          binding: 'frqNvl',
          header: String(t('ut.label.Frequency[Hz]', 'Frequency[Hz]')),
          width: 100,
          align: 'center',
          visible: false,
        },
        */
        {
          header: String(t('ut.label.Current', 'Current')),
          columns: [
            {
              binding: 'od2EcNvl',
              header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
              minWidth: 80,
              width: 97,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'od1EcNvl',
              header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
              minWidth: 80,
              width: 97,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        /*
        // [25.09.23] 화면에서 삭제 (고도화)
        {
          header: String(t('ut.label.동시가동율', '동시가동율')),
          columns: [
            {
              binding: 'smtmOprt',
              // header: String(t('ut.label.동시가동율[%]', '동시가동율[%]')),
              header: '[%]',
              width: 75,
              align: 'right',
            },
          ],
        },
        */
        {
          header: String(t('ut.label.Breaker', 'Breaker')),
          columns: [
            {
              binding: 'brkeCapa',
              header: String(t('ut.label.입력값 [A]', '입력값 [A]')),
              minWidth: 80,
              width: 97,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'optValCtn1',
              header: String(t('ut.label.계산값 [A]', '계산값 [A]')),
              minWidth: 80,
              width: 97,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          binding: 'plcClctYn',
          header: String(t('ut.label.PLC접지유무-', 'PLC접지유무-')),
          minWidth: 80,
          width: 100,
          align: 'center',
          visible: false,
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'elpwEqpCnctTpCdNm',
              header: '[Type]',
              minWidth: 60,
              width: 80,
              align: 'center',
            },
          ],
        },
        {
          header: String(t('ut.label.Capacity', 'Capacity')),
          columns: [
            {
              binding: 'elpwCapa',
              header: '[kW]',
              minWidth: 80,
              width: 83,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'elpwSumCapa',
              header: 'Sum',
              minWidth: 80,
              width: 83,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'elpwStndUseQty',
              header: '[kW]',
              minWidth: 70,
              width: 82,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          // [25.09.23] EUM의 Peak 값 표시
          header: String(t('ut.label.실사용량', '실사용량')),
          columns: [
            {
              binding: 'elpwEumMaxNvl',
              header: '[kW]',
              minWidth: 70,
              width: 82,
              isReadOnly: true,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Heat Capacity', 'Heat Capacity')),
          columns: [
            {
              binding: 'elpwHeatCapa',
              header: '[kW]',
              minWidth: 70,
              width: 88,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Demand Factor', 'Demand Factor')),
          columns: [
            {
              binding: 'dmdElpwTarfNvl',
              header: '[%]',
              minWidth: 70,
              width: 96,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Capacity_Sum', 'Capacity_Sum')),
          columns: [
            {
              binding: 'dmdElpwCapa',
              header: String(t('ut.label.(수용율 적용) [kW]', '(수용율 적용) [kW]')),
              minWidth: 100,
              width: 160,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.elpwEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.CDA', 'CDA')),
      collapseTo: 'cdaOtsdSizeValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'cdaOtsdSizeValCtn',
              header: 'OUT[Φ]',
              minWidth: 80,
              width: 85,
            },
            {
              binding: 'cdaInsdSizeValCtn',
              header: 'IN[Φ]',
              minWidth: 70,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'cdaDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'cdaPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'cdaPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'cdaCspCapa',
              header: '[Nl/min]',
              minWidth: 80,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'cdaCspSumCapa', // 수식계산 (= Qty * CDA Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 80,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'cdaStndUseQty',
              header: '[Nl/min]',
              minWidth: 80,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          // [25.09.23] EUM의 Peak 값 표시
          header: String(t('ut.label.실사용량', '실사용량')),
          columns: [
            {
              binding: 'cdaEumMaxNvl',
              header: '[kW]',
              minWidth: 70,
              width: 82,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.N2', 'N2')),
      collapseTo: 'nitrOtsdDmtrValCtn',
      columns: [
        {
          header: 'Size',
          columns: [
            {
              binding: 'nitrOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'nitrInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'nitrDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'nitrPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'nitrPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.AVG Consumption', 'AVG Consumption')),
          columns: [
            {
              binding: 'nitrCspCapa',
              header: '[Nl/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'nitrCspSumCapa',
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Peak Consumption', 'Peak Consumption')),
          columns: [
            {
              binding: 'nitrPeakCspCapa',
              header: '[Nl/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'nitrPeakCspSumCapa', // 수식 (= Qty * N2 Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'nitrStndUseQty',
              header: '[Nl/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.IW', 'IW')),
      collapseTo: 'iwOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'iwOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'iwInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'iwDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'iwTmprValCtn',
              header: '[℃]',
              minWidth: 70,
              width: 85,
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'iwPresValCtn', // [24.11.11] 숫자 -> 문자 변경 (데이터 검증 제외)
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'iwPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Drain', 'Drain')),
          columns: [
            {
              binding: 'iwDrnCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'iwDrnSumCapa',
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'iwCspCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'iwCspSumCapa', // 수식 (= Qty * IW Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'iwStndUseQty',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.IW for Emergency fire', 'FW')),
      collapseTo: 'frwtOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'frwtOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'frwtInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'frwtDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'frwtTmprValCtn',
              header: '[℃]',
              minWidth: 70,
              width: 83,
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'frwtPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'frwtPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Drain', 'Drain')),
          columns: [
            {
              binding: 'frwtDrnCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'frwtDrnSumCapa',
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'frwtCspCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'frwtCspSumCapa', // 수식 (= Qty * IW2 Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'frwtStndUseQty',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.PCW', 'PCW')),
      collapseTo: 'coolOtsdSizeValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'coolOtsdSizeValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'coolInsdSizeValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'coolDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'coolTmprValCtn',
              header: '[℃]',
              minWidth: 70,
              width: 85,
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'coolPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'coolPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'coolCspCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'coolCspSumCapa', // 수식 (= Qty * PCW Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'coolStndUseQty',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.WW', 'WW')),
      collapseTo: 'wwOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'wwOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'wwInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'wwDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'wwTmprValCtn',
              header: '[℃]',
              minWidth: 70,
              width: 85,
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'wwPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'wwPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'wwCspCapa',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'wwCspSumCapa',
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'wwStndUseQty',
              header: '[ℓ/min]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.Steam', 'Steam')),
      collapseTo: 'stemOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'stemOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'stemInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'stemDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Condensator connection', 'Condensator connection')),
          columns: [
            {
              binding: 'stemCwtSize',
              header: 'size [Φ]',
              minWidth: 70,
              width: 96,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'stemCwtDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 96,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'stemPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'stemPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 100,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'stemCspCapa',
              header: '[t/hr]',
              minWidth: 70,
              width: 85,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'stemCspSumCapa', // 수식 (= Qty * Steam Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 85,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'stemStndUseQty',
              header: '[t/hr]',
              minWidth: 70,
              width: 85,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.NG', 'NG')),
      collapseTo: 'lngOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'lngOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'lngInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'lngDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Pressure', 'Pressure')),
          columns: [
            {
              binding: 'lngPresValCtn',
              header: '[Bar]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'lngPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'lngCspCapa',
              header: '[Nm3/hr]',
              minWidth: 70,
              width: 110,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'lngCspSumCapa', // 수식 (= Qty * NG Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 90,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'lngStndUseQty',
              header: '[Nm3/hr]',
              minWidth: 70,
              width: 108,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.Exhaust air [Return(to Air Handling Unit)]', 'Return Air')),
      collapseTo: 'exhaOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'exhaOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'insdExhaInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'insdExhaCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 85,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Collector Type', 'Collector Type')),
          columns: [
            {
              binding: 'insdExhaCllrTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 93,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'insdExhaTmprValCtn',
              header: '[℃]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'insdExhaPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'insdExhaCspCapa', // 기계 조건부 필수 대상값
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'insdExhaCspSumCapa', // 수식 (= Qty * Exhaust air Return Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'insdExhaStndUseQty',
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.Exhaust air [Ventilation(to Outside)]', 'Exhaust air')),
      collapseTo: 'otsdExhaOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'otsdExhaOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'otsdExhaInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'otsdExhaCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Collector Type', 'Collector Type')),
          columns: [
            {
              binding: 'otsdExhaCllrTpCd', // [25.09.23] 추가
              header: '[Type]',
              minWidth: 70,
              width: 93,
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'otsdExhaTmprValCtn', // [25.09.23] 추가
              header: '[℃]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'otsdExhaPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'otsdExhaCspCapa', // 기계 조건부 필수 대상값
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'otsdExhaCspSumCapa', // 수식 (= Qty * Exhaust air [Ventilation Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'otsdExhaStndUseQty',
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.Supply Air', 'Supply air')),
      collapseTo: 'suarOtsdDmtrValCtn',
      columns: [
        {
          header: String(t('ut.label.Size', 'Size')),
          columns: [
            {
              binding: 'suarOtsdDmtrValCtn',
              header: 'OUT[Φ]',
              minWidth: 70,
              width: 85,
            },
            {
              binding: 'suarInsdDmtrValCtn',
              header: 'IN[Φ]',
              minWidth: 60,
              width: 75,
            },
          ],
        },
        {
          header: String(t('ut.label.Connection', 'Connection')),
          columns: [
            {
              binding: 'suarDevcCnctTpCdNm',
              header: '[Type]',
              minWidth: 70,
              width: 90,
              align: 'left',
            },
          ],
        },
        {
          header: String(t('ut.label.Tempurature', 'Tempurature')),
          columns: [
            {
              binding: 'suarTmprValCtn', // [25.09.23] 추가
              header: '[℃]',
              minWidth: 70,
              width: 80,
            },
          ],
        },
        {
          header: String(t("ut.label.Q'ty", "Q'ty")),
          columns: [
            {
              binding: 'suarPntCnt',
              header: '[Point]',
              minWidth: 70,
              width: 92,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          header: String(t('ut.label.Consumption', 'Consumption')),
          columns: [
            {
              binding: 'suarCspCapa', // 기계 조건부 필수 대상값
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
            {
              binding: 'suarCspSumCapa', // 수식 (= Qty * Supply air Consumption[ℓ/min])
              header: 'Sum',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
        {
          /**
           * [25.09.23] 표준사용량 로직 변경
           * - 표준설비코드 기준 라이브러리의 평균값 노출
           * - 라이브러리의 값이 없는 경우 대상에서 제외 (0 포함)
           */
          header: String(t('ut.label.표준사용량', '표준사용량')),
          columns: [
            {
              binding: 'suarStndUseQty',
              header: '[CMH]',
              minWidth: 70,
              width: 95,
              align: 'right',
              cellTemplate: (params) =>
                Number(params.item.machEqpQty || 0) < 1
                  ? DefaultNoneNumberCellTemplate(params)
                  : DefaultZeroNumberCellTemplate(params),
            },
          ],
        },
      ],
    },
    {
      header: String(t('ut.label.Equipment Specification', 'Equipment Specification')),
      collapseTo: 'eqpDimWthLen',
      isCollapsed: true,
      columns: [
        {
          header: String(t('ut.label.Dimension[mm]', 'Dimension[mm]')),
          columns: [
            {
              binding: 'eqpDimWthLen',
              header: String(t('ut.label.Width', 'Width')),
              minWidth: 70,
              width: 190,
              align: 'right',
              cellTemplate: DefaultZeroNumberCellTemplate,
            },
            {
              binding: 'eqpDimDpthNvl',
              header: String(t('ut.label.Depth', 'Depth')),
              minWidth: 70,
              width: 100,
              align: 'right',
              cellTemplate: DefaultZeroNumberCellTemplate,
            },
            {
              binding: 'eqpDimHigtNvl',
              header: String(t('ut.label.Height', 'Height')),
              minWidth: 70,
              width: 100,
              align: 'right',
              cellTemplate: DefaultZeroNumberCellTemplate,
            },
          ],
        },
        {
          header: String(t('ut.label.Weight[kg]', 'Weight[kg]')),
          columns: [
            {
              binding: 'eqpWgt',
              header: String(t('ut.label.전체중량', '전체중량')),
              minWidth: 70,
              width: 110,
              align: 'right',
              cellTemplate: DefaultZeroNumberCellTemplate,
            },
          ],
        },
      ],
    },
    {
      binding: 'rmk',
      header: String(t('ut.label.Remark', 'Remark')),
      minWidth: 70,
      width: 100,
    },
    {
      binding: 'dataInsUserNm',
      header: String(t('com.label.작성자', '작성자')),
      minWidth: 70,
      width: 100,
      align: 'center',
    },
    {
      binding: 'dataInsDtm',
      header: String(t('com.label.작성일자', '작성일자')),
      minWidth: 70,
      width: 120,
      align: 'center',
    },
    {
      binding: 'dataUpdUserNm',
      header: String(t('com.label.수정자', '수정자')),
      minWidth: 70,
      width: 100,
      align: 'center',
    },
    {
      binding: 'dataUpdDtm',
      header: String(t('com.label.수정일자', '수정일자')),
      minWidth: 70,
      width: 120,
      align: 'center',
    },
  ];

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        if (e.target instanceof HTMLButtonElement) {
          const id = e.target.id;
          if ('machEqpNm' === id) {
            if (item.machEqpNm && item.tagIssuYn === 'Y') {
              if (item.utmId && item.planProcId && item.utmSeq) {
                setHookupTagCondition({
                  utmId: item.utmId,
                  planProcId: item.planProcId,
                  utmSeq: item.utmSeq,
                  tagDivsCd: 'MECH',
                });
                setOpenHookupTagPopup(true);
              }
            }
          }
          if ('elpwEqpNm' === id) {
            if (item.elpwEqpNm && item.tagIssuYn === 'Y') {
              if (item.utmId && item.planProcId && item.utmSeq) {
                setHookupTagCondition({
                  utmId: item.utmId,
                  planProcId: item.planProcId,
                  utmSeq: item.utmSeq,
                  tagDivsCd: 'ELEC',
                });
                setOpenHookupTagPopup(true);
              }
            }
          }
        }
      }
    });
  };

  const btnDetailExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '상세요약';
    book.saveAsync(getExcelFileName(t('ut.label.상세요약', '상세요약')));
  };

  const excludePin = useMemo(() => {
    const convertFlat = (acc, cur) => {
      if ('columns' in cur && Array.isArray(cur?.columns)) {
        return cur.columns.reduce(convertFlat, acc);
      }
      return ![
        'utmId',
        'prdnProcCdNm',
        'bizAreaNm',
        'eqclNm',
        'stndEqpId',
        'dtalProcCd',
        'bldgFloCd',
        'istlLocNm',
        'prdnLnNm',
        'prdnPrlLnNm',
        'eqpMainNm',
        'eqpMchNm',
        'eqpUntNm',
        'machEqpNm',
        'elpwEqpNm',
        'machEqpQty',
        'elpwEqpQty',
      ].includes(cur?.binding)
        ? [...acc, cur?.binding]
        : acc;
    };

    return (layoutDefinition || []).reduce(convertFlat, []);
  }, [layoutDefinition]);

  return (
    <>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={detailRowData}
        height={360}
        frozenColumns={13}
        excludePin={excludePin}
        excludeFilter={['utmSeq', 'prdnProcCdNm', 'eqclNm']}
        initialized={onInitialized}
        isReadOnly={true}
        isSelector={false}
      />
      {isOpenHookupTagPopup && (
        <UtMatrixHookupTagPopup
          open={isOpenHookupTagPopup}
          close={() => setOpenHookupTagPopup(false)}
          condition={hookupTagCondition}
        />
      )}
    </>
  );
};

export default React.forwardRef(DetailSummaryContent);
